import { Box, Card, CardContent, Typography } from "@mui/material";
import Chart from "react-apexcharts";
import { useEffect, useState } from "react";
import { formatAmount } from "../../../helper/formatAmount";
import { getChartDataList } from "../../../services/customer-services/customerDashboardService";

const DashboardChart = ({ clientId, fetchOnlyIfClientId = false }) => {
  const [chartData, setChartData] = useState({ series: [], categories: [] });

  const getChartData = async () => {
    if (fetchOnlyIfClientId && !clientId) return;

    try {
      const response = await getChartDataList(clientId);
      if (response?.isSuccess) {
        setChartData({
          series: response.data?.series || [],
          categories: response.data?.categories || [],
        });
      } else {
        setChartData({ series: [], categories: [] });
      }
    } catch {
      setChartData({ series: [], categories: [] });
    }
  };

  useEffect(() => {
    getChartData();
  }, [clientId]);

  const calculateMaxValue = () => {
    try {
      const allDataPoints = chartData?.series?.flatMap((s) => s.data) || [];
      if (!allDataPoints.length) return 100000;
      const maxValue = Math.max(...allDataPoints.filter(Number.isFinite));
      return Number.isFinite(maxValue) ? Math.ceil(maxValue / 100000) * 100000 : 100000;
    } catch {
      return 100000;
    }
  };

  const roundedMax = calculateMaxValue();
  const tickAmount = 7;

  const options = {
    chart: {
      type: "bar",
      height: 350,
      stacked: false,
      toolbar: { show: false },
      width: "100%",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        // Reduced from 55% to 45% to ensure the border radius has space and isn't clipped
        columnWidth: "70%", 
        borderRadius: 0, // Slightly reduced to prevent "flat" sides on thin bars
      },
    },
    dataLabels: { enabled: false },
    // Changed width to 0. Transparent strokes with width > 0 often cause horizontal clipping
    stroke: { show: false, width: 0 }, 
    xaxis: {
      categories: chartData.categories || [],
      labels: { rotate: -45, style: { fontSize: "10px" }, hideOverlappingLabels: true },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { formatter: formatAmount },
      min: 0,
      max: roundedMax,
      tickAmount,
    },
    grid: {
      show: true,
      borderColor: "#C9CBCD",
      strokeDashArray: 4,
      padding: { top: 0, right: 10, bottom: 0, left: 10 },
    },
    states: { hover: { filter: { type: "none" } }, active: { filter: { type: "none" } } },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: ["#826320", "#40330D"],
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100],
      },
    },
    colors: ["#F5D01C", "#000000"],
    legend: { position: "top", horizontalAlign: "right", offsetY: -10 },
    tooltip: {
      custom: ({ series, dataPointIndex, w }) => {
        const label = w?.globals?.labels?.[dataPointIndex];
        const seriesNames = w?.config?.series?.map((s) => s.name);
        const values = series
          .map((s, i) => {
            const val = s?.[dataPointIndex];
            const colorBox =
              i === 0
                ? `background: linear-gradient(to right, #F5D01C, #826320);`
                : `background: linear-gradient(to bottom, #000000, #40330D);`;
            return `
              <div style="margin: 4px 0;">
                <span style="
                  display: inline-block;
                  ${colorBox}
                  height: 12px;
                  width: 12px;
                  border-radius: 50%;
                  margin-right: 4px;
                "></span>
                <span style="font-weight: 500;">${seriesNames?.[i]}:</span> ${val?.toLocaleString()} €
              </div>`;
          })
          .join("");
        return `<div style="padding: 8px 12px; border-radius: 6px;">
          <strong style="font-size: 14px;">${label}</strong>
          ${values}
        </div>`;
      },
    },
    responsive: [
      {
        breakpoint: 1200,
        options: {
          plotOptions: { bar: {  } },
          legend: { position: "bottom" },
        },
      },
      {
        breakpoint: 768,
        options: {
          // Narrower columnWidth for mobile ensures bars don't overlap or clip each other
          plotOptions: { bar: { } },
          xaxis: { labels: { rotate: -30, style: { fontSize: "9px" } } },
          legend: { position: "bottom" },
        },
      },
    ],
  };

  return (
    <Box sx={{ paddingTop: "1.5rem" }}>
      <Card variant="outlined" sx={{ background: "#fff", padding: "10px 0", borderRadius: "10px" }}>
        <CardContent sx={{ background: "#fff" }}>
          {chartData?.series?.length ? (
            <Box sx={{ width: "100%" }}>
              <Chart options={options} series={chartData.series} type="bar" height={350} />
            </Box>
          ) : (
            <Box
              sx={{
                height: 350,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontFamily: "Poppins",
              }}
            >
              <Typography variant="h6">No chart data available.</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardChart;